import { FormEvent, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SECTIONS } from "@/lib/site";
import { PageHeader } from "@/components/Doodles";
import { useCraftName } from "@/lib/affiliate";
import { GuidesCard } from "@/components/GuidesCard";

interface Settings { amazon_tag: string; craft_name: string; craft_deeplink: string }
interface Pick { id: string; name: string; url: string; store: string; note: string; sections: string[]; active: boolean }
interface Row { key: string; clicks: number }
interface Stats { total: number; by_store: Row[]; top_supplies: Row[]; top_pages: Row[]; top_picks: Row[] }

function SettingsCard() {
  const [s, setS] = useState<Settings | null>(null);
  useEffect(() => { api.get("/admin/affiliate/settings").then((r) => setS(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  if (!s) return <div className="paper-card p-6 text-sm">Loading…</div>;
  const save = async (e: FormEvent) => {
    e.preventDefault();
    try { setS((await api.put("/admin/affiliate/settings", s)).data); toast.success("Affiliate settings saved"); } catch (ex) { toast.error(formatApiError(ex)); }
  };
  const field = (k: keyof Settings, label: string, ph: string, hint: string) => (
    <div>
      <label className="label" htmlFor={`aff-${k}`}>{label}</label>
      <input id={`aff-${k}`} className="field" value={s[k]} placeholder={ph} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`aff-${k}-input`} />
      <p className="mt-1 text-xs text-[#23324A]/60">{hint}</p>
    </div>
  );
  return (
    <form onSubmit={save} className="paper-card space-y-4 p-6" data-testid="aff-settings">
      <p className="font-display text-lg font-black text-[#23324A]">Your affiliate IDs</p>
      {!s.amazon_tag && <p className="rounded-xl bg-[#FDE6DF] px-3 py-2 text-xs font-semibold text-[#23324A]" data-testid="aff-placeholder-note">No Amazon ID yet. Links still work, but don't earn until you add it.</p>}
      {field("amazon_tag", "Amazon Associates tracking ID", "yourname-20", "Amazon Associates → Account → Tracking IDs")}
      {field("craft_name", "Craft store name", "Michaels", "Shown on supply links, e.g. 'Amazon · Michaels'")}
      {field("craft_deeplink", "Craft store deep link (from your affiliate network)", "https://network.example/deeplink?id=123&murl={url}", "Leave empty to link straight to the store. Must contain {url}.")}
      <button className="btn-coral" type="submit" data-testid="aff-settings-save">Save</button>
    </form>
  );
}

const EMPTY = { name: "", url: "", store: "amazon", note: "", sections: ["all"], active: true };

function PickForm({ onSaved }: { onSaved: () => void }) {
  const [f, setF] = useState(EMPTY);
  const toggle = (slug: string) => {
    const next = f.sections.includes(slug) ? f.sections.filter((x) => x !== slug) : [...f.sections.filter((x) => slug === "all" ? false : x !== "all"), slug];
    setF({ ...f, sections: next.length ? next : ["all"] });
  };
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try { await api.post("/admin/affiliate/picks", f); toast.success("Added to Supplies I use"); setF(EMPTY); onSaved(); } catch (ex) { toast.error(formatApiError(ex)); }
  };
  return (
    <form onSubmit={submit} className="mt-5 space-y-3 rounded-2xl border-2 border-dashed border-[#23324A]/30 p-4" data-testid="pick-form">
      <div className="grid gap-3 sm:grid-cols-2">
        <input className="field" required placeholder="Product name, e.g. 65 lb white cardstock" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="pick-name-input" />
        <select className="field" value={f.store} onChange={(e) => setF({ ...f, store: e.target.value })} data-testid="pick-store-select">
          <option value="amazon">Amazon</option><option value="craft">Craft store</option><option value="other">Other store</option>
        </select>
      </div>
      <input className="field" required placeholder="Your affiliate product link (https://…)" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="pick-url-input" />
      <input className="field" placeholder="Why you like it (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="pick-note-input" />
      <div className="flex flex-wrap gap-2">
        {[{ slug: "all", short: "Every page" }, ...SECTIONS].map((s) => (
          <button type="button" key={s.slug} className={`chip ${f.sections.includes(s.slug) ? "chip-active" : ""}`} onClick={() => toggle(s.slug)} data-testid={`pick-section-${s.slug}`}>{s.short}</button>
        ))}
      </div>
      <button className="btn-coral" type="submit" data-testid="pick-add-btn"><Plus className="h-4 w-4" strokeWidth={3} />Add product</button>
    </form>
  );
}

function PicksCard({ onChange }: { onChange: () => void }) {
  const [picks, setPicks] = useState<Pick[]>([]);
  const load = useCallback(() => { api.get("/admin/affiliate/picks").then((r) => { setPicks(r.data); onChange(); }).catch((e) => toast.error(formatApiError(e))); }, [onChange]);
  useEffect(() => { load(); }, [load]);
  const remove = async (p: Pick) => {
    if (!window.confirm(`Remove "${p.name}" from Supplies I use?`)) return;
    try { await api.delete(`/admin/affiliate/picks/${p.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e)); }
  };
  const craft = useCraftName();
  const storeName = (s: string) => (s === "amazon" ? "Amazon" : s === "craft" ? craft : "Other store");
  const label = (p: Pick) => p.sections.map((s) => s === "all" ? "Every page" : SECTIONS.find((x) => x.slug === s)?.short).join(", ");
  return (
    <div className="paper-card min-w-0 p-6" data-testid="aff-picks">
      <p className="font-display text-lg font-black text-[#23324A]">Supplies I use</p>
      <p className="mt-1 text-sm text-[#23324A]/70">Products you actually use. They show in a box on the sections you pick and on their articles.</p>
      <ul className="mt-4 space-y-2">
        {picks.length === 0 && <li className="text-sm text-[#23324A]/60" data-testid="picks-empty">No products yet.</li>}
        {picks.map((p) => (
          <li key={p.id} className="flex items-center gap-3 rounded-xl border-2 border-[#23324A]/15 bg-white px-3 py-2" data-testid={`pick-row-${p.id}`}>
            <span className="min-w-0 flex-1"><span className="block truncate font-semibold text-[#23324A]">{p.name}</span><span className="text-xs text-[#23324A]/60">{storeName(p.store)} · {label(p)}</span></span>
            <button onClick={() => remove(p)} aria-label="Delete" className="text-[#23324A]/60 hover:text-[#F08A6C]" data-testid={`pick-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
      <PickForm onSaved={load} />
    </div>
  );
}

function StatList({ title, rows, testId }: { title: string; rows: Row[]; testId: string }) {
  return (
    <div className="min-w-0" data-testid={testId}>
      <p className="label">{title}</p>
      {rows.length === 0 ? <p className="text-xs text-[#23324A]/55">No clicks yet</p> : (
        <ul className="space-y-1 text-sm text-[#23324A]">{rows.map((r) => <li key={r.key} className="flex justify-between gap-3"><span className="min-w-0 truncate">{r.key}</span><b>{r.clicks}</b></li>)}</ul>
      )}
    </div>
  );
}

function StatsCard() {
  const [st, setSt] = useState<Stats | null>(null);
  const craft = useCraftName();
  useEffect(() => { api.get("/admin/affiliate/stats", { params: { days: 30 } }).then((r) => setSt(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  return (
    <div className="paper-card min-w-0 p-6" data-testid="aff-stats">
      <p className="font-display text-lg font-black text-[#23324A]">Clicks, last 30 days</p>
      <p className="mt-1 font-display text-4xl font-black text-[#F08A6C]" data-testid="aff-total-clicks">{st ? st.total : "…"}</p>
      <p className="text-xs text-[#23324A]/60">Clicks only. Sales and earnings show in your Amazon and network dashboards.</p>
      {st && (
        <div className="mt-5 grid gap-5 sm:grid-cols-2">
          <StatList title="By store" rows={st.by_store.map((r) => ({ ...r, key: r.key === "amazon" ? "Amazon" : r.key === "craft" ? craft : "Other" }))} testId="stats-by-store" />
          <StatList title="Top pages" rows={st.top_pages} testId="stats-top-pages" />
          <StatList title="Top supplies" rows={st.top_supplies} testId="stats-top-supplies" />
          <StatList title="Top picks" rows={st.top_picks} testId="stats-top-picks" />
        </div>
      )}
    </div>
  );
}

export default function AffiliatePage() {
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  return (
    <div data-testid="affiliate-page">
      <PageHeader title="Affiliate links" kicker="Amazon + craft store · supply links · clicks" />
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="min-w-0 space-y-6"><SettingsCard /><StatsCard /></div>
        <div className="min-w-0 space-y-6"><PicksCard onChange={bump} /><GuidesCard version={version} /></div>
      </div>
    </div>
  );
}
